<template>
  <div>
    <div v-if="displayState === VALID">
      <!-- メタ情報 -->
      <div class="md-title title-space">
        基本情報
        <span
          class="badge"
          :style="{
            borderColor: executionTypeColor,
            color: executionTypeColor,
          }"
        >
          {{ selectedRow.reservationId ? '予約実行' : '即時実行' }}
        </span>
      </div>
      <md-list class="meta-info-list">
        <!-- タイトル（予約実行のデータのみ表示） -->
        <md-list-item v-if="selectedRow.reservationId">
          <div class="md-list-item-text border-bottom">
            <div class="title-edit-wrapper">
              <div class="title-content">
                <span class="meta-label">タイトル</span>
                <h3 class="meta-title">{{ selectedRow.title }}</h3>
              </div>
              <md-button style="width: 30px" @click="openEditTitleDialog">
                <md-icon style="cursor: pointer;">edit</md-icon>
              </md-button>
            </div>
          </div>
        </md-list-item>

        <!-- メニュー情報（アバター + メニュー名） -->
        <md-list-item class="menu-info-item">
          <div class="menu-info-wrapper border-bottom">
            <span class="meta-label">メニュー</span>
            <span
              class="badge"
              :style="{
                borderColor: serviceTypeColor,
                color: serviceTypeColor,
              }"
            >
              {{ selectedRow.menuDataType }}
            </span>
            <div class="menu-avatar-wrapper">
              <md-avatar class="md-list-item-avatar">
                <img
                  :src="selectedRow.menuImageUrl"
                  :alt="selectedRow.menuLongName"
                />
              </md-avatar>
              <span class="menu-name">{{ selectedRow.menuLongName }}</span>
            </div>
          </div>
        </md-list-item>

        <!-- 実行日/実行予定日 -->
        <md-list-item>
          <div class="md-list-item-text">
            <div
              style="display: flex; justify-content: space-between; width: 100%;"
            >
              <div class="border-bottom meta-field-item">
                <span class="meta-label">{{ executedDateLabel }}</span>
                <p>{{ formattedDate }}</p>
              </div>
              <div
                v-if="activeTab === 'reservation' && selectedRow.cycleType"
                class="border-bottom meta-field-item"
                style="margin-left: 8px;"
              >
                <span class="meta-label">実行周期</span>
                <p>{{ cycleTypeName }}</p>
              </div>
            </div>
          </div>
        </md-list-item>
      </md-list>
      <!-- 分析条件 -->
      <div class="md-title title-space">分析条件</div>
      <div class="filter-summary-wrapper">
        <div class="filter-summary">
          <md-list class="filter-summary-body md-double-line">
            <slot name="filter-summary">
              <component
                v-for="p of filterSummaryProperties"
                :key="p.name"
                :is="`filter-summary-${p.name}`"
                :scroll-to-id="p.name"
                v-bind="p.filter"
                class="filter-summary-component"
              />
            </slot>
          </md-list>
        </div>
      </div>
      <!-- 操作ボタン群 -->
      <div class="summary-button">
        <span v-if="selectedRow.queryId" class="tooltip-wrapper">
          <gl-button @click="handleQueryFilterDumpDownload">
            問い合わせ用添付データをダウンロードする
          </gl-button>
          <md-tooltip md-direction="top">
            エラーや分析結果についてお問い合わせいただく際、このファイルを添付していただくと解析がスムーズになります
          </md-tooltip>
        </span>
        <span
          v-if="
            selectedRow.reservationId &&
              selectedRow.status === 'NextReservationFailed'
          "
          class="tooltip-wrapper"
        >
          <md-button @click="handleCloseReservation" class="close-button">
            対応済みにする
          </md-button>
          <md-tooltip md-direction="top">
            新規で予約を作成済みの場合は、こちらをクリックして対応済みにしてください。
          </md-tooltip>
        </span>
        <span
          v-else-if="
            selectedRow.reservationId &&
              canDeleteReservation(selectedRow.status)
          "
          class="tooltip-wrapper"
        >
          <md-button @click="handleDeleteReservation" class="delete-button">
            削除する
          </md-button>
        </span>
        <span
          v-if="
            selectedRow.reservationId &&
              selectedRow.status === 'Waiting' &&
              selectedRow.cycleType &&
              selectedRow.cycleType !== 'ONCE'
          "
          class="tooltip-wrapper"
        >
          <md-button @click="handlePauseReservation" class="pause-button">
            一時停止
          </md-button>
          <md-tooltip md-direction="top">
            定期実行を一時停止します（再開時は編集画面から再設定します）
          </md-tooltip>
        </span>

        <span class="tooltip-wrapper">
          <md-button
            class="md-raised md-primary"
            @click="handleSubmit()"
            :disabled="queryState === 'disabled'"
          >
            {{ submitButtonText }}
          </md-button>
          <md-tooltip v-if="queryState === 'disabled'" md-direction="top">
            {{ submitButtonTooltipText }}
          </md-tooltip>
        </span>
      </div>
    </div>
    <div v-else-if="displayState === EXPIRED">
      <!-- 選択されていても表示できる行が無ければ表示しない -->
      <span
        gl-tooltip="選択された分析の有効期限は切れています"
        gl-tooltip-direction="right"
        class="tooltip-wrapper"
      >
        <md-button :disabled="true" class="md-raised md-primary">
          {{ submitButtonText }}
        </md-button>
      </span>
    </div>
    <div v-else>
      <!-- 選択行無しの場合は何も表示しない -->
    </div>

    <!-- タイトル編集ダイアログ -->
    <md-dialog :md-active.sync="showEditTitleDialog">
      <md-dialog-title>タイトルを編集</md-dialog-title>
      <md-dialog-content>
        <!-- エラー表示 -->
        <md-list-item v-if="titleUpdateError" class="error-item">
          <md-icon class="error-icon">error</md-icon>
          <span class="error-message">{{ titleUpdateError }}</span>
        </md-list-item>

        <md-field>
          <label>タイトル</label>
          <md-input v-model="editingTitle" type="text"></md-input>
        </md-field>
      </md-dialog-content>

      <md-dialog-actions>
        <spinner
          v-if="isUpdatingTitle"
          :diameter="24"
          :stroke="2"
          class="update-spinner"
        />
        <template v-else>
          <md-button class="md-primary" @click="closeEditTitleDialog"
            >キャンセル</md-button
          >
          <div class="update-button-wrapper">
            <md-button
              class="md-primary md-raised"
              @click="updateTitle"
              :disabled="isUpdatingTitle"
            >
              更新する
            </md-button>
          </div>
        </template>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>
import { kebabCase } from 'lodash'
import moment from 'moment'
import download from '@/util/download'
import { toBase64 } from '@/util/string'
import { filter } from 'rxjs/operators'
import DialogConfirmation from '@/components/DialogConfirmation.vue'
import Spinner from '@/components/Spinner.vue'
import { canDeleteReservation } from '@/const/statusConfig'

function loadSummaryComponents() {
  const requireComponent = require.context(
    '../components/',
    false,
    /FilterSummary[\w-]+\.vue$/,
  )
  return requireComponent
    .keys()
    .map(fileName => {
      const name = fileName.replace(/^\.\//, '').replace(/\.\w+$/, '')

      return {
        name,
        import: () => import(`../components/${name}.vue`),
      }
    })
    .reduce((o, c) => ({ ...o, [c.name]: c.import }), {})
}

export default {
  components: {
    Spinner,
    ...loadSummaryComponents(),
  },
  data() {
    return {
      VALID: 'VALID',
      EXPIRED: 'EXPIRED',
      DISABLE: 'DISABLE',
      showDeleteConfirmationDialog: false,
      showEditTitleDialog: false,
      editingTitle: '',
      isUpdatingTitle: false,
      titleUpdateError: '',
    }
  },
  props: {
    selectedRow: {
      type: Object,
    },
    queryState: {
      type: String,
      // 'result-available': 分析結果確認可能
      // 'reuse-available': 分析条件を再利用可能
      // 'edit-available': 予約条件編集可能
      // 'disabled': 遷移不可
      default: 'disabled',
    },
    hasPermission: {
      type: Boolean,
      default: true,
    },
    handleSubmit: {
      type: Function,
      default: () => {},
    },
    deleteReservation: {
      type: Function,
      default: () => {},
    },
    closeReservation: {
      type: Function,
      default: () => {},
    },
    pauseReservation: {
      type: Function,
      default: () => {},
    },
    activeTab: {
      type: String,
      default: 'history',
    },
  },
  computed: {
    /**
     * フィルターサマリの表示プロパティ群を取得する
     */
    filterSummaryProperties() {
      if (this.selectedRow) {
        return Object.entries(this.selectedRow.queryFilter)
          .filter(entry => entry[1].order !== undefined)
          .map(([name, filter]) => ({
            name: filter.summaryKey
              ? kebabCase(filter.summaryKey)
              : kebabCase(name),
            filter,
          }))
          .sort((p1, p2) => p1.filter.order - p2.filter.order)
      }
      return undefined
    },
    /**
     * 表示するべき内容（ステータス）を返す
     */
    displayState() {
      // 行選択＆履歴として条件が残っている場合。
      if (this.filterSummaryProperties === undefined) {
        return this.DISABLE
      } else if (this.filterSummaryProperties.length === 0) {
        return this.EXPIRED
      } else {
        return this.VALID
      }
    },
    /**
     * 遷移ボタン表示文言
     */
    submitButtonText() {
      if (this.queryState === 'edit-available') {
        if (this.selectedRow && this.selectedRow.status === 'Paused') {
          return '条件を修正して再開'
        }
        return '予約条件を編集する'
      } else if (this.queryState === 'reuse-available') {
        if (
          this.selectedRow &&
          this.selectedRow.status === 'NextReservationFailed'
        ) {
          return '次回予約を作成する'
        }
        return '分析条件を再利用する'
      } else {
        // result-availableとdisabledの場合は「分析結果を確認する」
        return '分析結果を確認する'
      }
    },
    /**
     * 遷移ボタンツールチップ表示文言
     */
    submitButtonTooltipText() {
      if (this.selectedRow && this.selectedRow.queryId) {
        return '権限不足または結果無効により閲覧不可'
      } else {
        return '実行中またはエラーのため編集できません'
      }
    },
    /**
     * 実行日/実行予定日のラベル
     */
    executedDateLabel() {
      if (!this.selectedRow) return ''
      // 履歴タブの場合は「startedAt」、予約タブの場合は「scheduledDate」
      return this.selectedRow.startedAt ? '実行日' : '実行予定日'
    },
    /**
     * フォーマットされた実行日/実行予定日
     */
    formattedDate() {
      if (!this.selectedRow) return ''
      const date = this.selectedRow.startedAt || this.selectedRow.scheduledDate
      if (!date) return ''

      return this.selectedRow.startedAt
        ? moment(date).format('YYYY-MM-DD HH:mm:ss')
        : moment(date).format('YYYY-MM-DD')
    },
    /**
     * サービスタイプ（POS/ID-POS）の色
     */
    serviceTypeColor() {
      if (!this.selectedRow) return '#000'
      switch (this.selectedRow.menuDataType) {
        case 'ID-POS':
          return '#40c4ff'
        case 'POS':
          return '#f8aa37'
        default:
          return '#000'
      }
    },
    /**
     * 実行タイプ（予約実行/即時実行）の色
     */
    executionTypeColor() {
      if (!this.selectedRow) return '#000'
      // 予約実行: 予約登録ボタンと同じ, 即時実行: 分析実行ボタンと同じ
      return this.selectedRow.reservationId ? '#dfac40' : '#448aff'
    },
    /**
     * CycleType を日本語に変換
     */
    cycleTypeName() {
      if (!this.selectedRow) return ''
      const cycleType = this.selectedRow.cycleType
      switch (cycleType) {
        case 'ONCE':
          return '今回のみ'
        case 'WEEKLY':
          return '毎週'
        case 'MONTHLY':
        case 'MONTHLY_EXACT':
          return '毎月'
        default:
          return cycleType || ''
      }
    },
  },
  methods: {
    canDeleteReservation, // ステータスが削除可能（Waiting/Paused/RequiresUpdate）かを判定
    handleQueryFilterDumpDownload() {
      const dump = {
        queryId: this.selectedRow.queryId,
        menuCode: this.selectedRow.menu,
        queryFilter: this.selectedRow.queryFilter,
        startedAt: this.selectedRow.startedAt,
        completedAt: this.selectedRow.completedAt,
        userId: this.selectedRow.userId,
      }

      const filename = `【提出用】BiZOOPeクエリダンプ_${
        this.selectedRow.menu
      }_${this.selectedRow.menuLongName}.giggle-dump`
      const fileContents = toBase64(JSON.stringify(dump))

      download(new Blob([fileContents]), filename)
    },
    handleDeleteReservation() {
      this.$dialog
        .open(DialogConfirmation, {
          message: '予約削除してよろしいですか？',
          buttonTextOk: '削除する',
          buttonTextCancel: 'キャンセル',
        })
        .pipe(filter(response => response))
        .subscribe(async () => {
          await this.deleteReservation(this.selectedRow.reservationId)
        })
    },
    handleCloseReservation() {
      this.$dialog
        .open(DialogConfirmation, {
          message:
            '次回予約の失敗を対応済みにします。新規で予約を作成済みの場合は対応済みにするをクリックしてください。',
          buttonTextOk: '対応済みにする',
          buttonTextCancel: 'キャンセル',
        })
        .pipe(filter(response => response))
        .subscribe(async () => {
          await this.closeReservation(this.selectedRow.reservationId)
        })
    },
    handlePauseReservation() {
      this.$dialog
        .open(DialogConfirmation, {
          message:
            'この予約を一時停止します。一時停止中は予約実行されません。編集画面から再登録するまで再開されません。',
          buttonTextOk: '一時停止',
          buttonTextCancel: 'キャンセル',
        })
        .pipe(filter(response => response))
        .subscribe(async () => {
          await this.pauseReservation(this.selectedRow.reservationId)
        })
    },
    // タイトル編集ダイアログを開く
    openEditTitleDialog() {
      this.editingTitle = this.selectedRow.title
      this.showEditTitleDialog = true
    },
    // タイトル編集ダイアログを閉じる
    closeEditTitleDialog() {
      this.showEditTitleDialog = false
      this.editingTitle = ''
      this.titleUpdateError = ''
    },
    // タイトルを更新する
    async updateTitle() {
      if (!this.editingTitle.trim()) {
        return
      }

      this.isUpdatingTitle = true
      try {
        await this.$repositories
          .get('queryReservation')
          .updateTitle(this.selectedRow.reservationId, this.editingTitle)

        // API呼び出しが成功したら、selectedRow を更新
        this.selectedRow.title = this.editingTitle
        this.closeEditTitleDialog()
      } catch (error) {
        // エラー表示 - axios のエラーレスポンスからメッセージを取得
        this.titleUpdateError = error.response
          ? error.response.data
          : error.message || 'タイトルの更新に失敗しました'
      } finally {
        this.isUpdatingTitle = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
$header-height: 72px;
$filter-summary-width: 320px;

/* フィルターサマリーコンテナ：ホバー時の背景色変更を無くす */
.filter-summary-wrapper /deep/ .wrapper:hover {
  background: none !important;
}

/* フィルターサマリーのパディング設定 */
.filter-summary-wrapper {
  padding-top: var(--spacing-small);
  padding-left: var(--spacing-medium);
  padding-right: var(--spacing-large);
}

/* フィルターサマリーの個別アイテムの余白 */
.filter-summary-wrapper /deep/ .wrapper {
  margin-left: var(--spacing-small);
}

/* フィルター条件コンテナ：各条件を表示するボックス */
/deep/ .container {
  color: black !important;
  padding: 2px !important;

  display: flex;
  align-items: flex-start;
  width: 100%;
  overflow: hidden;
  border-bottom: 1px solid var(--color-border);
}

/* フィルター条件のアイコンエリア */
/deep/ .icon-area {
  flex: 0 0 auto;
}

/* フィルター条件のアイコン */
/deep/ .gl-icon {
  color: black !important;
}

/* フィルター条件のコンテンツエリア（右側）*/
/deep/ .content {
  flex: 1;
  min-width: 0;
}

/* フィルター条件のテキストサマリー */
/deep/ .summary {
  color: black !important;

  width: 100%;
  overflow-wrap: break-word;
  word-break: break-all;
}

/* サマリーの行レイアウト：flexで左右に配置 */
/deep/ .summary > div {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
}

/* サマリーの奇数列（ラベル）：左側の固定幅 */
/deep/ .summary > div > div:nth-child(odd) {
  width: 10%;
}

/* サマリーの偶数列（値）：右側の可変幅（比較項目の場合のみ） */
/deep/ .summary > div > div:nth-child(even) {
  width: 90%;
  text-align: left !important;
  padding: 2px 0;
}

/* サマリー下部の操作ボタン群 */
.summary-button {
  display: flex;
  justify-content: flex-end;
}

/* ボタンのツールチップコンテナ */
.tooltip-wrapper {
  display: inline-block;
}
/* 完了ボタン */
.close-button {
  color: #fff !important;
  background-color: var(--color-primary) !important;
}

/* 削除ボタン */
.delete-button {
  margin-left: var(--spacing-medium);
  background-color: #bdbdbd !important;
}

/* 一時停止ボタン */
.pause-button {
  margin-left: var(--spacing-medium);
  color: #fff !important;
  background-color: #ff9800 !important;
}

/* メタ情報セクション：基本情報の表示リスト */
.meta-info-list {
  border-bottom: 1px solid var(--color-border);
  padding: var(--spacing-medium);
}

/* メタ情報リストのアイテム */
.meta-info-list /deep/ .md-list-item {
  height: auto !important;
  min-height: auto !important;
}

/* メタ情報リストのテキストコンテンツ */
.meta-info-list /deep/ .md-list-item-text {
  width: 100%;
  padding: 0 !important;
  height: auto !important;
  line-height: 1.2;
  max-height: none !important;
}

/* メタ情報リストのテキストの子要素 */
.meta-info-list /deep/ .md-list-item-text > span {
  display: block;
}

/* メタ情報のタイトルテキスト */
.meta-title {
  margin: 0;
  font-size: var(--font-size-normal);
  font-weight: 600;
  color: #333;
  line-height: 1.2;
}

/* タイトル編集エリアのレイアウト */
.title-edit-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-small);
  width: 100% !important;
}

/* タイトルコンテンツのコンテナ */
.title-content {
  min-width: 0;
}

/* メニュー情報のラッパー */
.menu-info-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px 8px;
  width: 100%;
  border-bottom: 1px solid var(--color-border);
  padding-bottom: var(--spacing-small);
}

/* メニュータイプ・実行タイプを表示するバッジ */
.badge {
  font-size: var(--font-size-small);
  border: 1px solid;
  padding: 2px 6px;
  border-radius: 3px;
  font-weight: bold;
  display: inline-block;
  flex-shrink: 0;
  white-space: nowrap;
  width: fit-content;
  height: fit-content;
  line-height: 1.3;
}

/* メニューアバター・メニュー名を並べるコンテナ */
.menu-avatar-wrapper {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--spacing-small);
}

/* メニューアバター画像のスタイル */
.menu-info-item /deep/ .md-list-item-avatar {
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
  flex-shrink: 0;
  box-sizing: border-box !important;
}

/* メニューアバター画像 */
.menu-info-item /deep/ .md-list-item-avatar img {
  width: 32px;
  height: 32px;
}

/* メニュー名のテキスト */
.menu-name {
  font-size: var(--font-size-normal);
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* メタラベル（「メニュー」「実行日」など） */
.meta-label {
  font-size: var(--font-size-small);
  color: #666;
  margin-right: 4px;
}

/* 下部ボーダー付きのコンテナ */
.border-bottom {
  border-bottom: 1px solid var(--color-border);
  padding-bottom: var(--spacing-small);
}

.meta-field-item {
  padding-bottom: 4px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

/* セクションタイトル：「基本情報」「分析条件」などのヘッダー */
.title-space {
  padding: var(--spacing-small) var(--spacing-medium);
  margin-bottom: 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-small);
}

/* エラーメッセージコンテナ */
.error-item {
  display: flex !important;
  align-items: center !important;
  background-color: #ffebee;
  border-left: 3px solid #f44336;
  padding: var(--spacing-small) var(--spacing-medium) !important;
  margin-bottom: var(--spacing-medium);
  border-radius: 2px;
}

/* エラーアイコン */
.error-icon {
  color: #f44336;
  margin-right: var(--spacing-small);
  flex-shrink: 0;
}

/* エラーメッセージテキスト */
.error-message {
  color: #c62828;
  font-size: var(--font-size-small);
  line-height: 1.4;
  word-break: break-word;
}

/* タイトル更新ボタンのラッパー */
.update-button-wrapper {
  display: flex;
  align-items: center;
  gap: var(--spacing-small);
}

/* タイトル更新時の読み込みスピナー */
.update-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: var(--spacing-medium);
}
</style>
